'use client';

import { useTranslations } from 'next-intl';
import { useProfile } from '@/shared/api/hooks/use-user';
import type { Project } from '@/types/project';

/**
 * What the account-nav status chrome should render (multi-product model).
 *
 * Currently produced:
 * - `pro_plan` / `facade_pack` — the CURRENT building is unlimited (an
 *   effective Pro Plan activation or purchased Facade Kit). Surfaces a premium
 *   crown + the English brand `label` (from the pack source). Off any building
 *   a globally-premium account still reads the crown via `access_badge`.
 * - `tokens` — every other case: a non-kit building runs on the token wallet,
 *   for EVERYONE (tokens are universal now — free users carry a positive
 *   daily-grant balance). The nav paints the numeric from the token balance.
 * - `none` — profile not seeded yet.
 */
export type AccountChromeKind =
  | 'tokens'
  | 'pro_plan'
  | 'facade_pack'
  | 'none';

export interface AccountChrome {
  kind: AccountChromeKind;
  /** Short brand label for the crown kinds; absent for `tokens` (uses the number). */
  label?: string;
}

/**
 * Resolve the account-nav status chrome (multi-product model). `currentProject`
 * is the building the user is currently scoped to (the nav surfaces resolve it
 * from the route). The glyph is PER-BUILDING: a building whose Facade Kit /
 * Pro Plan activation is effective reads UNLIMITED (crown), every other building
 * runs on the token wallet — for EVERYONE (tokens are universal now).
 *
 * Without a building context (the Account page) it falls back to the global
 * `access_badge` so a premium account still reads the crown there.
 */
export function useAccountChrome(
  currentProject?: Project | null
): AccountChrome {
  const tPacks = useTranslations('packs');
  const profile = useProfile().data;
  const accessBadge = profile?.access_badge;

  // Profile not seeded yet — don't flash anything.
  if (profile === undefined) {
    return { kind: 'none' };
  }

  // Per-building crown: an effective pack/Pro Plan activation on the CURRENT
  // building makes it unlimited (server `pack.is_unlimited`), regardless of
  // global account status. The brand label follows the PACK SOURCE, never the
  // account badge — on a purchased-Kit building of a Pro-Plan account the
  // badge label would say "Pro Plan" under a `facade_pack` kind (kind/label
  // mismatch). Both brands are English everywhere (brand terms).
  const pack = currentProject?.attributes?.pack;
  if (pack?.is_unlimited) {
    return pack.source === 'pro_plan'
      ? { kind: 'pro_plan', label: tPacks('proPlanStatus') }
      : { kind: 'facade_pack', label: tPacks('headerStatus') };
  }

  // No building context (Account page): a premium account (Pro Plan / owns
  // a Kit) reads the crown everywhere via the server-resolved badge.
  if (!currentProject && accessBadge) {
    return { kind: accessBadge.kind, label: accessBadge.label };
  }

  // A building without a kit → the token wallet, for everyone. Free-tier users
  // carry a positive daily-grant balance; there is no separate "N free designs"
  // chrome anymore (tokens are universal — server `account_chrome` is premium |
  // tokens only). The numeric itself is read from the token balance by the nav.
  return { kind: 'tokens' };
}

/**
 * Compact Account nav glyph (bottom tab bar). PER-BUILDING like the sidebar
 * chrome (multi-product model): inside a kit building it reads `premium` (crown),
 * inside a non-kit building it reads `tokens` (balance), and off any building it
 * falls back to the global server-resolved account status. Delegates to
 * {@link useAccountChrome} so the tab and the sidebar can never disagree.
 */
export type AccountNavGlyphKind = 'premium' | 'tokens' | 'none';

export interface AccountNavGlyph {
  kind: AccountNavGlyphKind;
}

export function useAccountNavGlyph(
  currentProject?: Project | null
): AccountNavGlyph {
  const chrome = useAccountChrome(currentProject);

  switch (chrome.kind) {
    // A kit building (or a globally-premium account off any building) → crown.
    case 'pro_plan':
    case 'facade_pack':
      return { kind: 'premium' };
    // Non-kit building, or a non-premium account → the token wallet balance
    // (rendered from the token balance by the nav surface).
    case 'tokens':
      return { kind: 'tokens' };
    // Profile not seeded yet (or nothing to surface) → person glyph.
    default:
      return { kind: 'none' };
  }
}
